'use client'

import { useState, useEffect } from 'react'
import { Sun, Moon, Monitor, Sparkles } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTheme } from '../theme-provider'

type Theme = 'light' | 'dark' | 'system'

export default function FloatingThemeToggle() {
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const themes = [
    {
      value: 'light' as Theme,
      icon: Sun,
      label: 'Light',
      gradient: 'from-amber-400 via-yellow-500 to-orange-500',
    },
    {
      value: 'dark' as Theme,
      icon: Moon,
      label: 'Dark',
      gradient: 'from-slate-700 via-slate-800 to-slate-900',
    },
    {
      value: 'system' as Theme,
      icon: Monitor,
      label: 'Auto',
      gradient: 'from-emerald-400 via-teal-500 to-cyan-500',
    },
  ]

  const toggleTheme = () => {
    const currentIndex = themes.findIndex(t => t.value === theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    const nextTheme = themes[nextIndex].value;
    setTheme(nextTheme);
  };

  const currentThemeDetails = themes.find(t => t.value === theme)
  const CurrentIcon = currentThemeDetails?.icon || Sun

  if (!mounted) return null

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div className="relative w-14 h-14 group transition-transform duration-500 hover:scale-105">
        <button
          onClick={toggleTheme}
          className="relative w-full h-full overflow-hidden rounded-2xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 shadow-xl group-hover:shadow-2xl transition-shadow duration-500 flex items-center justify-center"
          aria-label="Toggle theme"
        >
          <div className={`absolute inset-0 bg-gradient-to-br ${currentThemeDetails?.gradient} opacity-100 scale-100 transition-all duration-500`}></div>
          <div className="absolute inset-0 rounded-2xl bg-white/10 dark:bg-slate-900/10 scale-0 group-active:scale-100 transition-transform duration-200"></div>
          <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-slate-200/50 dark:ring-slate-700/50"></div>
        </button>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-6 h-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={theme}
                initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                transition={{ duration: 0.3 }}
                className="absolute"
              >
                <CurrentIcon className="h-6 w-6 text-white" />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <Sparkles className="absolute -top-1 -right-1 h-3 w-3 text-amber-400 dark:text-slate-400 animate-pulse pointer-events-none" />
      </div>
    </div>
  )
}